import chroma from "chroma-js";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { setColorBarRange } from "store/slices/allBuildings/dynamicDashboard/dynamicDashboardSlice";

import { findDataRange, isNumeric } from "../../utils/dataManipulation";
import {
  aggregatedFirstValue,
  getItemColor,
  getMapPointValueBasedColor,
  settingsOptions,
} from "../../utils/dynamicDashboard";
import { cn, getCSSVar } from "../../utils/styling";
import { CategoricalColorBar, ColorBar } from "./Colorbar";

export const createMiniMapNumericalStrategy = ({
  aggregationFunction,
  appName,
  chartType,
  config,
  data,
  dataPoint,
  IdColumnName,
  isItemHovered,
  miniMapObjectsNameKey,
  somethingIsHovered,
}) => {
  const useLogic = () => {
    const dispatch = useDispatch();

    let flattenData = aggregationFunction({
      aggregateColumns: [dataPoint],
      data: data,
      groupByColumns: [miniMapObjectsNameKey, IdColumnName, "latitude", "longitude"],
    });

    const colorScale = chroma.scale([
      getCSSVar("--core-color-cathode-green-600"),
      getCSSVar("--core-color-tangerine-400"),
      getCSSVar("--core-color-mystic-red-500"),
    ]);

    const userMinMax = config?.[settingsOptions.COLOR_BAR_RANGE];

    const { dataWithColorAndValueMemoized, max, min } = useMemo(() => {
      const nonNullValues = flattenData?.filter((point) => isNumeric(point[dataPoint]));
      const { max: dataMax, min: dataMin } =
        findDataRange({
          data: nonNullValues ?? [],
          dataKeys: [dataPoint],
        }) ?? {};
      const min = userMinMax?.[0] ?? dataMin;
      const max = userMinMax?.[1] ?? dataMax;

      const dataWithColorAndValueMemoized = flattenData?.map((point) => {
        const opacity =
          somethingIsHovered &&
          isItemHovered &&
          !isItemHovered({
            chartType,
            diagrams: [],
            row: point,
          })
            ? 0
            : 1;
        return {
          ...point,
          center: [point.latitude, point.longitude],
          color: getMapPointValueBasedColor({
            colorScale,
            max,
            min,
            opacity,
            value: point[dataPoint],
          }),
          name: point[miniMapObjectsNameKey],
          value: point[dataPoint],
        };
      });

      dispatch(setColorBarRange({ appName, chartId: config.id, range: { max: dataMax, min: dataMin } }));

      return {
        dataWithColorAndValueMemoized,
        max,
        min,
      };
    }, [
      data,
      dataPoint,
      aggregationFunction,
      userMinMax?.[0],
      userMinMax?.[1],
      miniMapObjectsNameKey,
      somethingIsHovered,
      IdColumnName,
      isItemHovered,
    ]);

    return {
      colorBarProps: {
        colorScale,
        max,
        min,
      },
      flattenData,
      miniMapData: dataWithColorAndValueMemoized,
    };
  };

  return { ColorBar, useLogic };
};

export const createMiniMapCategoricalStrategy = ({
  chartType,
  config,
  data,
  dataPoint,
  getColor,
  getColumnSortFunction,
  IdColumnName,
  isItemHovered,
  miniMapObjectsNameKey,
  onLegendSelect,
  somethingIsHovered,
}) => {
  const useLogic = () => {
    let flattenData = aggregatedFirstValue({
      aggregateColumns: [dataPoint],
      data: data,
      groupByColumns: [miniMapObjectsNameKey, IdColumnName, "latitude", "longitude"],
    });

    const allUniqueValues = [...new Set(flattenData?.map((point) => point[dataPoint]))];

    const valuesSortFunction = (a, b) => {
      if (getColumnSortFunction)
        return getColumnSortFunction({
          aValue: a[dataPoint],
          bValue: b[dataPoint],
          dataKey: dataPoint,
        });
      return a < b ? -1 : 1;
    };
    allUniqueValues.sort(valuesSortFunction);

    const { dataWithColorAndValueMemoized } = useMemo(() => {
      const dataWithColorAndValueMemoized = flattenData?.map((point) => {
        const valueIndex = allUniqueValues.indexOf(point[dataPoint]);
        const isHidden =
          somethingIsHovered &&
          isItemHovered &&
          !isItemHovered({
            chartType,
            diagrams: [],
            row: point,
          });

        return {
          ...point,
          center: [point.latitude, point.longitude],
          color: isHidden
            ? "rgba(0,0,0,0)"
            : getColor?.({
                chartType,
                dataPoint,
                quantity: point[dataPoint],
              }) || getItemColor(valueIndex),
          isHidden,
          name: point[miniMapObjectsNameKey],
          value: point[dataPoint],
        };
      });

      return {
        dataWithColorAndValueMemoized,
      };
    }, [data, getColor, dataPoint, miniMapObjectsNameKey, somethingIsHovered, IdColumnName, isItemHovered]);

    const colorsData = Array.from(
      new Map(
        dataWithColorAndValueMemoized?.map((item) => [
          item.value,
          {
            className: cn(item.isHidden && "text-lds-foreground-disabled"),
            color: item.color,
            label: item.value,
          },
        ])
      ).values()
    );

    colorsData?.sort((a, b) => (a.label > b.label ? 1 : -1));

    return {
      colorBarProps: { colorsData, onLegendClick: (name) => onLegendSelect({ config, params: { name } }) },
      flattenData,
      miniMapData: dataWithColorAndValueMemoized,
    };
  };

  return { ColorBar: CategoricalColorBar, useLogic };
};
